/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: 284 lines
 * - Adding: ~4 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts, auth components
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    GOOGLE_SIGNUP_EXISTING_EMAIL_MESSAGE
 * Date:         2025-12-07
 * Modified By:  Cursor
 *
 * Description:
 * Show server-provided error message when Google signup fails, instead of a generic toast.
 * Extracts error message from backend response to display user-friendly messages.
 *
 * Related Screens / Modules:
 * - client/src/pages/auth/AuthPage.tsx
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    AUTH_ROUTES_SIGNIN_SIGNUP
 * Date:         2025-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Align auth routing so /, /signin, and /signup behave consistently and can be deep-linked.
 * Redirect unauthenticated root to /signin and prevent logged-in users from seeing auth screens.
 *
 * Related Screens / Modules:
 * - client/src/App.tsx
 * - client/src/pages/auth/AuthPage.tsx
 * - client/src/components/AuthModal.tsx
 * - client/src/components/auth/SignupForm.tsx
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    AUTH_ROUTES_V1
 * Date:         2025-12-06
 * Modified By:  Cursor (assisted by ChatGPT)
 *
 * Description:
 * Add direct /signin and /signup routes and new AuthPage wrapper, while
 * preserving existing AuthModal behavior for non-auth pages.
 *
 * Related Screens / Modules:
 * - Client → src/App.tsx
 * - Client → src/pages/auth/AuthPage.tsx
 * - Client → src/components/AuthModal.tsx (light refactor only if needed)
 */

// [Auth routes / new full-page auth wrapper / 2025-12-06]
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    UI_STANDARDIZATION
 * Date:         2025-01-XX
 * Modified By:  Cursor
 *
 * Description:
 * Replaced direct toast.* calls with centralized notify helper for consistent messaging.
 *
 * Related Screens / Modules:
 * - client/src/pages/auth/AuthPage.tsx
 * - client/src/utils/notifications.ts
 */

import { notify } from "../../utils/notifications";
import AuthTabs from "../../components/auth/AuthTabs";
import LoginForm from "../../components/auth/LoginForm";
import SignupForm from "../../components/auth/SignupForm";
import { apiUrl } from "../../config/environment";

// 
type AuthTab = "signin" | "signup";

interface AuthPageProps {
  initialTab?: AuthTab;
}

const AuthPage: React.FC<AuthPageProps> = ({ initialTab }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const pathTab: AuthTab = useMemo(() => {
    return location.pathname.includes("signup") ? "signup" : "signin";
  }, [location.pathname]);

  const [activeTab, setActiveTab] = useState<AuthTab>(initialTab || pathTab);

  // [Auth routes / sync tab with URL / 2025-12-06]
  // Keep local state synchronized with URL
  useEffect(() => {
    setActiveTab(pathTab);
  }, [pathTab]);

  // [2025-01-XX] Google signup T&C: Store T&C values for Google signup
  const [googleSignupTosAccepted, setGoogleSignupTosAccepted] = useState(false);
  const [googleSignupMarketingOptIn, setGoogleSignupMarketingOptIn] = useState(false);
  // [2025-01-XX] Google signup form data: Store form values for Google signup
  const [googleSignupFormData, setGoogleSignupFormData] = useState<{
    firstName?: string;
    lastName?: string;
    phone?: string;
    address?: string;
  } | null>(null);

  // [Auth routes / Google signup handler / 2025-12-06]
  // [2025-01-XX] Google signup T&C: Modified to accept T&C values from modal
  const signupWithGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // 1) Fetch profile (email, name, picture, sub)
        // [2026-02-11] cookie-auth: Explicitly disable credentials for Google API
        const profileRes = await axios.get("https://www.googleapis.com/oauth2/v3/userinfo", {
          headers: {
            Authorization: `Bearer ${tokenResponse.access_token}`,
          },
          withCredentials: false, // Google API doesn't accept credentials
        });

        const p = profileRes.data;
        // 2) Console logs requested (email and other fields)
        console.log("Google profile (signup):", p);
        console.log("email:", p.email);
        console.log("email_verified:", p.email_verified);
        console.log("given_name:", p.given_name);
        console.log("family_name:", p.family_name);
        console.log("googleId (sub):", p.sub);
        console.log("picture:", p.picture);

        // 3) Send to your backend to upsert a user and get your app JWT
        //    This example sends both the access token and the profile so your server
        //    can verify/normalize as needed.
        // [2025-01-XX] Google signup T&C: Include tosAccepted and marketingOptIn in request
        // [2025-01-XX] Google signup form data: Include form values in request
        // [2026-02-11] Cookie auth: Added withCredentials for cookie handling
        const authRes = await axios.post(`${apiUrl}/api/users/auth/google`, {
          flow: "access_token",
          accessToken: tokenResponse.access_token,
          profile: {
            sub: p.sub,
            email: p.email,
            email_verified: p.email_verified,
            given_name: p.given_name,
            family_name: p.family_name,
            picture: p.picture,
          },
          provider: "google",
          intent: "signup",
          tosAccepted: googleSignupTosAccepted,
          marketingOptIn: googleSignupMarketingOptIn,
          // Pass form values if available (will override Google profile data when provided)
          firstName: googleSignupFormData?.firstName,
          lastName: googleSignupFormData?.lastName,
          phone: googleSignupFormData?.phone,
          address: googleSignupFormData?.address,
        }, { withCredentials: true });

        const appJwt = authRes.data; // your backend should return your app JWT
        if (typeof appJwt !== "string" || !appJwt) {
          console.error("Unexpected /auth/google response:", authRes.data);
          // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
          notify.error(null, "We couldn't complete signup. Please try again");
          return;
        }

        // [2026-02-11] Cookie auth: Token is now set via HTTP-only cookie by server
        navigate("/");
        handleAuthSuccess();
      } catch (err: any) {
        console.error("Google signup error:", err);
        // [2025-12-07] Google signup: show backend message when signup is blocked for existing email
        // Extract server error message from response, fallback to generic message if not available
        const serverMessage =
          err?.response?.data?.message || err?.response?.data?.errors?.[0]?.message;
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error(err, serverMessage || "We couldn't create your account with Google. Please try again");
      } finally {
        // [2025-01-XX] Google signup T&C: Reset T&C values after signup attempt
        setGoogleSignupTosAccepted(false);
        setGoogleSignupMarketingOptIn(false);
        // [2025-01-XX] Google signup form data: Reset form data after signup attempt
        setGoogleSignupFormData(null);
      }
    },
    onError: (err) => {
      console.error("Google login popup failed:", err);
      // [2025-01-XX] Google signup T&C: Reset T&C values on error
      setGoogleSignupTosAccepted(false);
      setGoogleSignupMarketingOptIn(false);
      // [2025-01-XX] Google signup form data: Reset form data on error
      setGoogleSignupFormData(null);
    },
  });

  // [2025-01-XX] Google signup T&C: Handler to initiate Google signup with T&C values
  // [2025-01-XX] Google signup form data: Handler accepts form data for Google signup
  const handleGoogleSignup = (
    tosAccepted: boolean,
    marketingOptIn: boolean,
    formData?: {
      firstName?: string;
      lastName?: string;
      phone?: string;
      address?: string;
    },
  ) => {
    setGoogleSignupTosAccepted(tosAccepted);
    setGoogleSignupMarketingOptIn(marketingOptIn);
    // Store form data if provided
    setGoogleSignupFormData(formData || null);
    signupWithGoogle();
  };

  const handleTabChange = (isSignup: boolean) => {
    const nextTab: AuthTab = isSignup ? "signup" : "signin";
    // [2025-01-27] auth-routes: keep tab state in sync with router
    setActiveTab(nextTab);
    const nextRoute = isSignup ? "/signup" : "/signin";
    navigate(nextRoute);
  };

  const handleAuthSuccess = () => {
    // [Auth routes / keep existing redirect behavior / 2025-12-06]
    // current behavior: redirect to dashboard/root
    navigate("/");
  };

  return (
    <div className="container d-flex justify-content-center align-items-center auth-page-min-height">
      {/* Top bar for sign in/sign up screens */}
      <div className="auth-top-bar">
        <img src="/assets/logo.svg" alt="UAVZone" className="auth-top-bar-logo" />
      </div>

      {/* [2026-02-07] style-extraction: moved marginTop to SCSS */}
      <div className="auth-page-container">
        {/* Welcome title - outside the card */}
        <h1 className="auth-welcome-title text-center">Welcome</h1>

        {/* Auth card content */}
        <div className={activeTab === "signup" ? "auth-signup-card" : "auth-card"}>
          {/* Tabs header */}
          <AuthTabs isSignup={activeTab === "signup"} onTabChange={handleTabChange} />

          {/* Form */}
          {activeTab === "signin" ? (
            <>
              <LoginForm
                navigate={navigate}
                onAuthSuccess={handleAuthSuccess}
                onClose={handleAuthSuccess}
              />
              {/* Secondary links */}
              <div className="text-center forgot-password-container">
                <Link to="/forgot" className="forgot-password-link">
                  Forgot Password?
                </Link>
              </div>
              <div className="text-center signup-prompt-container">
                <span className="signup-prompt">Don't have an account yet? </span>
                {/* [2025-01-27] auth-routes: use router navigation for deep-linking */}
                <Link to="/signup" className="btn p-0 border-0 bg-transparent signup-inline-link">
                  Sign up
                </Link>
              </div>
            </>
          ) : (
            <SignupForm
              navigate={navigate}
              onAuthSuccess={handleAuthSuccess}
              onClose={handleAuthSuccess}
              onGoogleSignup={handleGoogleSignup}
              onSwitchToLogin={() => navigate("/signin")}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
